import type { JobWithDetails } from "@/api/hooks/useJob";
import { useApi, useDelete } from "@mb-pro-ui/utils";
import { Add, Check, Delete, Refresh } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  alpha,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import { useMutation } from "@tanstack/react-query";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type TouchEvent,
} from "react";
import { useIntl } from "react-intl";

// Animáció alulról felfelé
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type SignatureCanvasProps = {
  onSave: (blob: Blob) => void;
  mimeType?: "image/png" | "image/jpeg";
};

function SignatureCanvas({
  onSave,
  mimeType = "image/png",
}: SignatureCanvasProps) {
  const { formatMessage } = useIntl();
  // const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = true;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      ctx.strokeStyle = "#000";
      ctx.lineWidth = isMobile ? 3 : 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("orientationchange", resize);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("orientationchange", resize);
    };
  }, [isMobile]);

  const getPos = (e: MouseEvent | TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e: MouseEvent | TouchEvent) => {
    setIsDrawing(true);
    const { x, y } = getPos(e);
    const ctx = canvasRef.current?.getContext("2d");
    ctx?.beginPath();
    ctx?.moveTo(x, y);
  };

  const draw = (e: MouseEvent | TouchEvent) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current?.getContext("2d");
    ctx?.lineTo(x, y);
    ctx?.stroke();
    setHasSignature(true);
  };

  const stop = () => {
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const save = () => {
    const canvas = canvasRef.current!;
    if (!hasSignature) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      onSave(blob);
    }, mimeType);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        overflow: "hidden",
        p: { xs: 0.5, sm: 1 },
      }}
    >
      <Box
        sx={{
          flex: 1,
          border: "2px dashed #ccc",
          borderRadius: 2,
          mb: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "100%",
            cursor: "crosshair",
            touchAction: "none",
            background: "#fff",
            borderRadius: "8px",
            display: "block",
          }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
          onTouchCancel={stop}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={clear}
          disabled={!hasSignature}
        >
          {formatMessage({ defaultMessage: "Remove" })}
        </Button>
        <Button
          variant="contained"
          startIcon={<Check />}
          onClick={save}
          disabled={!hasSignature}
        >
          {formatMessage({ defaultMessage: "Save" })}
        </Button>
      </Box>
    </Box>
  );
}

type SignatureFieldProps = {
  jobId: string;
  attachments: JobWithDetails["attachments"];
  refetch: () => void;
};

export function SignatureField({
  jobId,
  attachments,
  refetch,
}: SignatureFieldProps) {
  const { formatMessage } = useIntl();
  const api = useApi();
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  // const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = true;

  const uploadMutation = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      return api(`/actions/raptor/jobs/${jobId}/attach-file?type=signature`, {
        method: "POST",
        headers: {
          "Content-Type": file.type,
          "Content-Disposition": `attachment; filename="${encodeURIComponent(
            file.name,
          )}"`,
        },
        body: file,
      });
    },
    onSuccess: () => {
      refetch();
      setOpen(false); // A dialógus bezárása
    },
    // onError: (error: Error) => {
    //   enqueueSnackbar(
    //     formatMessage({
    //       defaultMessage: `Error saving signature: ${error.message}`,
    //     }),
    //     { variant: "error" },
    //   );
    // },
  });

  const handleSave = (blob: Blob) => {
    const signatureFile = new File([blob], "signature.png", {
      type: "image/png",
    });
    uploadMutation.mutate({ file: signatureFile });
  };

  const handleOpen = () => {
    setOpen(true);
    try {
      if (document.documentElement.requestFullscreen && isMobile) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
    } catch {}
  };

  const handleClose = () => {
    if (uploadMutation.isPending) {
      return;
    }
    setOpen(false);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  };

  const deleteMutation = useDelete("raptor/attachments", {
    onSuccess: () => {
      refetch();
    },
    // onError: (error: Error) => {
    //   setError(error.message);
    // },
  });

  const removeAttachment = useCallback(
    (attachmentId: string) => {
      deleteMutation.mutate(attachmentId);
    },
    [deleteMutation],
  );

  const hasSignatures = attachments?.some(
    (attachment) => attachment["attachment-type"] === "signature",
  );

  return (
    <>
      <Card sx={{ mt: 2 }}>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderRadius: 2,
            pb: "16px !important",
          }}
        >
          <Typography variant="raptor-sub-h">
            {formatMessage({ defaultMessage: "Signature" })}
          </Typography>
          <IconButton
            color="primary"
            onClick={handleOpen}
            sx={{ p: "4px" }}
            disabled={uploadMutation.isPending || deleteMutation.isPending}
          >
            <Add />
          </IconButton>
        </CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 1,
            p: hasSignatures ? 2 : 0,
            pt: 0,
          }}
        >
          {attachments
            ?.filter(
              (attachment) => attachment["attachment-type"] === "signature",
            )
            .map((attachment) => (
              <React.Fragment key={attachment.id}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    transition: "all 0.2s ease-in-out",
                    width: "100%",
                  }}
                >
                  <Box
                    key={attachment.id}
                    sx={{
                      width: 40,
                      height: 40,
                      border: "1px solid #ccc",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={attachment.url}
                      alt={formatMessage({ defaultMessage: "Signature" })}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => removeAttachment(attachment.id)}
                    color="error"
                    sx={{ display: attachment.own ? "inherit" : "none" }}
                  >
                    <Delete fontSize="small" color="error" />
                  </IconButton>
                </Box>
              </React.Fragment>
            ))}
        </Box>
      </Card>

      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        slots={{ transition: Transition }}
        sx={{
          "& .MuiDialog-paper": {
            margin: 0,
            maxHeight: "100vh",
            height: "100vh",
            width: "100vw",
          },
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            p: 0,
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <Typography variant="raptor-main-h">
              {formatMessage({ defaultMessage: "Create signature" })}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "text.primary",
                backgroundColor: "action.hover",
                "&:hover": {
                  backgroundColor: "action.selected",
                },
              }}
              aria-label={formatMessage({
                defaultMessage: "Close",
              })}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            p: 0,
            overflow: "hidden",
          }}
        >
          <SignatureCanvas onSave={handleSave} />
        </DialogContent>
      </Dialog>
    </>
  );
}
